import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import {
  public_service_provider_api_requests,
  service_provider_api_requests,
  SERVICE_PROVIDER_API,
} from "..";
import { BaseDto, FilterGroup, NormalFilter } from "../../util/baseDto";
import { useExport } from "../../util/useExport";
import { useNotification } from "../../../components/common/GlobalNotification";
import { useMemo } from "react";

const PATH = "/service-area";
const BASE_QUERY_KEY = SERVICE_PROVIDER_API + PATH;

export interface ServiceAreaItem extends BaseDto {
  id: number;
  city: string;
  stateId: number;
  postalCode?: string | null;
  hasLocality?: boolean;
  activeIndicator?: boolean | null;
  isActiveForSpRegistrationOnly?: boolean | null;
  imageUrl?: string | null;
}

export interface ServiceAreaApiResponse {
  metaData: {
    size: number;
    totalPages: number;
    page: number;
    totalElements: number;
  };
  data: ServiceAreaItem[];
}

export interface CreateServiceAreaRequest {
  city: string;
  stateId: number;
  postalCode?: string | null;
  hasLocality?: boolean;
  activeIndicator?: boolean;
  isActiveForSpRegistrationOnly?: boolean;
}

export interface UpdateServiceAreaRequest {
  city: string;
  stateId: number;
  postalCode?: string | null;
  hasLocality?: boolean;
  activeIndicator?: boolean;
  isActiveForSpRegistrationOnly?: boolean;
}

function buildServiceAreaFormData(request: Record<string, any>, file?: File | null): FormData {
  const formData = new FormData();
  formData.append("data", new Blob([JSON.stringify(request)], { type: "application/json" }));
  if (file) formData.append("file", file);
  return formData;
}

export interface CreateServiceAreaResponse {
  id: number;
  city: string;
  stateId: number;
  postalCode?: string | null;
  hasLocality?: boolean;
  activeIndicator?: boolean;
  isActiveForSpRegistrationOnly?: boolean;
}

const useCreateServiceArea = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      request,
      file,
    }: {
      request: CreateServiceAreaRequest;
      file?: File | null;
    }): Promise<CreateServiceAreaResponse> =>
      service_provider_api_requests.post(PATH, buildServiceAreaFormData(request, file)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Service area created successfully");
    },
    onError: () => {
      setErrorNotification("Failed to create service area");
    },
  });
};

const useUpdateServiceArea = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      id,
      request,
      file,
    }: {
      id: number;
      request: UpdateServiceAreaRequest;
      file?: File | null;
    }): Promise<CreateServiceAreaResponse> =>
      service_provider_api_requests.put(`${PATH}/${id}`, buildServiceAreaFormData(request, file)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Service area updated successfully");
    },
    onError: () => {
      setErrorNotification("Failed to update service area");
    },
  });
};

const useSearchServiceAreas = (
  size: number,
  page: number,
  city?: string,
  stateId?: number
) => {
  const filterGroup: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [
        ...(city ? [NormalFilter("city", "LIKE", city)] : []),
        ...(stateId ? [NormalFilter("stateId", "EQUALS", stateId)] : []),
      ],
    }),
    [city, stateId]
  );

  return useInfiniteQuery<ServiceAreaApiResponse, Error>({
    queryKey: [BASE_QUERY_KEY, "paged", city, stateId, size],
    queryFn: async ({ pageParam = page }) =>
      public_service_provider_api_requests.search(
        `${PATH}/public/search?page=${pageParam}&size=${size}`,
        filterGroup
      ),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metaData.page ?? 0;
      const totalPages = lastPage.metaData.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useGetServiceAreas = (sort: string) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "service-areas", sort],
    queryFn: async (): Promise<ServiceAreaApiResponse> => {
      const url = `${PATH}/public/all?size=100&sort=${sort}`;
      return public_service_provider_api_requests.get(url);
    },
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useGetServiceAreasByLocality = (sort: string) => {
  const filterGroup: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [
        NormalFilter("activeIndicator", "EQUALS", true),
        NormalFilter("hasLocality", "EQUALS", true),
      ],
    }),
    []
  );

  return useQuery({
    queryKey: [BASE_QUERY_KEY, "service-areas-by-locality", sort],
    queryFn: async (): Promise<ServiceAreaApiResponse> =>
      public_service_provider_api_requests.search(
        `${PATH}/public/search?page=0&size=100&sort=${sort}`,
        filterGroup
      ),
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useGetActiveServiceAreas = (sort: string) => {
  const filterGroup: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [NormalFilter("activeIndicator", "EQUALS", true)],
    }),
    []
  );

  return useQuery({
    queryKey: [BASE_QUERY_KEY, "active-service-areas", sort],
    queryFn: async (): Promise<ServiceAreaApiResponse> =>
      public_service_provider_api_requests.search(
        `${PATH}/public/search?page=0&size=100&sort=${sort}`,
        filterGroup
      ),
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
/** SP registration and SP My Account cities: active cities plus cities active for SP registration only. */
const useGetSpServiceAreas = (sort: string) => {
  const filterGroup: FilterGroup = useMemo(
    () => ({
      operator: "OR",
      criteria: [
        NormalFilter("activeIndicator", "EQUALS", true),
        NormalFilter("isActiveForSpRegistrationOnly", "EQUALS", true),
      ],
    }),
    []
  );

  return useQuery({
    queryKey: [BASE_QUERY_KEY, "sp-service-areas", sort],
    queryFn: async (): Promise<ServiceAreaApiResponse> =>
      public_service_provider_api_requests.search(
        `${PATH}/public/search?page=0&size=100&sort=${sort}`,
        filterGroup
      ),
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useGetServiceAreaById = (id: number) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "service-area", id],
    queryFn: async (): Promise<ServiceAreaItem> =>
      public_service_provider_api_requests.get(`${PATH}/public/${id}`),
    enabled: !!id,
    staleTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useDeleteServiceArea = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (id: number): Promise<void> =>
      service_provider_api_requests.delete(`${PATH}/${id}`),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [BASE_QUERY_KEY],
      });
      setSuccessNotification("Service area deleted successfully");
    },

    onError: () => {
      setErrorNotification("Failed to delete service area");
    },
  });
};

export const useExportServiceAreas = () =>
  useExport(
    (filterGroup) => public_service_provider_api_requests.export(`${PATH}/public/export`, filterGroup),
    "service-areas.xlsx",
    "Failed to export Service Areas",
  );

/** New hook — paginated active cities with images, backed by GET /public/cities */
const useGetPublicCities = (page: number, size: number = 20) => {
  return useInfiniteQuery<ServiceAreaApiResponse, Error>({
    queryKey: [BASE_QUERY_KEY, "public-cities", size],
    queryFn: async ({ pageParam = page }): Promise<ServiceAreaApiResponse> =>
      public_service_provider_api_requests.get(
        `${PATH}/public/cities?page=${pageParam}&size=${size}&sort=city,asc`
      ),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metaData.page ?? 0;
      const totalPages = lastPage.metaData.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export {
  BASE_QUERY_KEY as SERVICE_AREA_DETAILS_QUERY_KEY,
  useCreateServiceArea,
  useUpdateServiceArea,
  useSearchServiceAreas,
  useGetServiceAreas,
  useGetServiceAreaById,
  useGetActiveServiceAreas,
  useGetSpServiceAreas,
  useGetServiceAreasByLocality,
  useDeleteServiceArea,
  useGetPublicCities,
};
